// Publish icon button + menu, mirroring `GitFilesMenu` in shape so the two
// sit side-by-side in the Project Files header of BuilderEditor and in the
// per-row menu in AppsList.
//
// On click, fetches the latest publish status and shows: current state
// (live + age, building, failed), a Publish-now action, and a link to the
// CDN URL when a build has succeeded. Polls every 2s while a publish is in
// flight.

import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import LucideIcon from "../../../components/LucideIcon";
import type { BuilderApiClient } from "../api";
import type { PublishStatusResponse } from "../types";
import { formatRelativeTime } from "../../../utils";

interface Props {
  api: BuilderApiClient;
  appId: string;
  /** Optional: render a compact variant with no tooltip + no chip — used in
   * the per-row menu in AppsList where the IconButton is the only trigger. */
  size?: "small" | "medium";
}

const tooltipTitle = (
  status: PublishStatusResponse | null,
  publishedAgo: string | null,
  inFlight: boolean,
  hasPublished: boolean,
): string => {
  if (inFlight) {
    return status?.publishStatus === "building"
      ? "Publishing… building bundle"
      : "Publishing… queued";
  }
  if (hasPublished) return `Published ${publishedAgo ?? ""}`;
  return "Publish";
};

const actionLabel = (
  status: PublishStatusResponse | null,
  inFlight: boolean,
  hasPublished: boolean,
): string => {
  if (inFlight) {
    return status?.publishStatus === "building" ? "Building…" : "Queued…";
  }
  return hasPublished ? "Publish a new version" : "Publish";
};

const PublishMenu: React.FC<Props> = ({ api, appId, size = "small" }) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [status, setStatus] = useState<PublishStatusResponse | null>(null);
  const [publishing, setPublishing] = useState(false);
  const [snack, setSnack] = useState<
    { msg: string; severity: "success" | "error" | "info" } | null
  >(null);
  // Refs so the polling effect doesn't need to be re-attached on every change.
  const apiRef = useRef(api);
  const appIdRef = useRef(appId);
  apiRef.current = api;
  appIdRef.current = appId;

  const refreshStatus = useCallback(async () => {
    try {
      const s = await apiRef.current.getPublishStatus(appIdRef.current);
      setStatus(s);
    } catch {
      // 404 / feature-flag off — leave status null so the menu shows
      // a generic "publish" entry without misleading state.
      setStatus(null);
    }
  }, []);

  const open = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      setAnchor(e.currentTarget);
      void refreshStatus();
    },
    [refreshStatus],
  );

  const close = useCallback(() => setAnchor(null), []);

  // Background poll: as long as a publish is queued or building, refresh
  // every 2s — even when the menu is closed, so the icon tooltip + live
  // badge stay current.
  useEffect(() => {
    void refreshStatus();
  }, [refreshStatus]);

  useEffect(() => {
    const inFlight =
      status?.publishStatus === "queued"
      || status?.publishStatus === "building";
    if (!inFlight) return;
    const handle = window.setInterval(refreshStatus, 2000);
    return () => window.clearInterval(handle);
  }, [status?.publishStatus, refreshStatus]);

  const runPublish = useCallback(async () => {
    setPublishing(true);
    try {
      await apiRef.current.publishApp(appIdRef.current);
      setSnack({
        msg: "Publish started — bundling…",
        severity: "info",
      });
      // Optimistic flip so the icon spinner appears immediately.
      setStatus((prev) => (prev ? { ...prev, publishStatus: "queued" } : prev));
      await refreshStatus();
    } catch (e: any) {
      setSnack({
        msg: e?.response?.data?.error || e?.message || "Publish failed",
        severity: "error",
      });
    } finally {
      setPublishing(false);
    }
  }, [refreshStatus]);

  const inFlight =
    status?.publishStatus === "queued"
    || status?.publishStatus === "building"
    || publishing;
  const failed = status?.publishStatus === "failed";
  const hasPublished = !!status?.publishedBuildId;
  const publishedAgo = status?.publishedAt ? formatRelativeTime(status.publishedAt) : null;
  const publicUrl = `${window.location.origin}/x/${appId}`;

  const triggerIcon = inFlight ? (
    <CircularProgress size={16} />
  ) : (
    <Box sx={{ display: "flex", color: failed ? "error.main" : "inherit" }}>
      <LucideIcon name="Rocket" size={18} />
    </Box>
  );

  return (
    <>
      <Tooltip title={tooltipTitle(status, publishedAgo, inFlight, hasPublished)}>
        <IconButton size={size} onClick={open}>
          {triggerIcon}
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchor}
        open={!!anchor}
        onClose={close}
        slotProps={{ paper: { sx: { minWidth: 300, maxWidth: 380 } } }}
      >
        {hasPublished && (
          <MenuItem disabled sx={{ opacity: 1, "&.Mui-disabled": { opacity: 1 } }}>
            <Stack sx={{ width: "100%" }}>
              <Typography variant="caption" color="text.secondary">
                Published
              </Typography>
              <Typography variant="body2">
                Live · {publishedAgo ?? "just now"}
              </Typography>
              <Typography
                variant="caption"
                component="a"
                href={publicUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: "primary.main", textDecoration: "none" }}
                onClick={(e) => e.stopPropagation()}
              >
                Open published URL
              </Typography>
            </Stack>
          </MenuItem>
        )}

        {failed && (
          <MenuItem disabled sx={{ opacity: 1, "&.Mui-disabled": { opacity: 1 } }}>
            <Stack sx={{ width: "100%" }}>
              <Typography variant="caption" color="error">
                Last build failed
              </Typography>
              {status?.lastPublishError && (
                <Box
                  sx={{
                    mt: 0.5,
                    maxHeight: 180,
                    overflow: "auto",
                    whiteSpace: "pre-wrap",
                    fontFamily: "monospace",
                    fontSize: 11,
                    color: "text.secondary",
                    backgroundColor: "action.hover",
                    p: 1,
                    borderRadius: 1,
                  }}
                >
                  {status.lastPublishError}
                </Box>
              )}
            </Stack>
          </MenuItem>
        )}

        {(hasPublished || failed) && <Divider />}

        <MenuItem
          disabled={inFlight}
          onClick={() => {
            close();
            void runPublish();
          }}
        >
          <ListItemText
            primary={actionLabel(status, inFlight, hasPublished)}
            secondary={
              hasPublished
                ? "Builds a fresh production bundle and uploads it to the CDN."
                : "Builds a production bundle and hosts it on the CDN."
            }
          />
        </MenuItem>

        {hasPublished && (
          <MenuItem
            onClick={() => {
              close();
              window.open(publicUrl, "_blank", "noopener");
            }}
          >
            <ListItemText primary="Open published URL" />
            <Box sx={{ ml: 1, opacity: 0.6, display: "flex" }}>
              <LucideIcon name="ExternalLink" size={18} />
            </Box>
          </MenuItem>
        )}
      </Menu>

      <Snackbar
        open={!!snack}
        autoHideDuration={6000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {snack ? (
          <Alert severity={snack.severity} onClose={() => setSnack(null)}>
            {snack.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </>
  );
};

export default PublishMenu;
